import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Overline } from "@/components/ui-bits";
import {
  BookOpen, Play, Pause, Download, FileText, ArrowRight, DollarSign,
  Bot, Wallet, Rocket, Megaphone, Radar, Target, ShoppingCart, CreditCard, Gauge,
  LayoutDashboard, CheckCircle2, ChevronRight, Volume2, VolumeX, Compass,
} from "lucide-react";

const IMG = (name) => `/tutorial/${name}.png`;
const AUDIO = (name) => `/tutorial/audio/scene-${name}.mp3`;
const DOC_HTML = "/kleen-g-tutorial.html";
const DOC_MD = "/kleen-g-tutorial.md";

// ── Video-style walkthrough (auto-plays real screenshots with narration) ──
const SCENES = [
  { key: "console", step: "01", title: "Operator Console", img: "console",
    caption: "Welcome to Kleen-G. This is your Operator Console — mission control. See treasury, revenue, spend and fleet health, plus a live stream of everything your agents do." },
  { key: "agents", step: "02", title: "Agents Fleet", img: "agents",
    caption: "The Agents Fleet is your AI workforce. Spin up wallet-backed agents with a mission and a budget. Each earns, spends under policy, and can spawn child agents." },
  { key: "treasury", step: "03", title: "Treasury & Guardrails", img: "treasury",
    caption: "Fund the treasury once, then set spend caps and approval thresholds. Money only ever moves within the rules you define." },
  { key: "deployments", step: "04", title: "Deployment Pipeline", img: "deployments",
    caption: "Agents build & ship real sites to Vercel with SSL. You approve production deploys; cost is charged only when a build actually ships." },
  { key: "lead-sources", step: "05", title: "Lead Sources", img: "lead-sources",
    caption: "Fill your funnel. Discover real local businesses through Google Places, and win freelance gigs automatically with the Prospector agent." },
  { key: "outreach", step: "06", title: "Local Outreach", img: "outreach",
    caption: "The Local Outreach engine auto-generates a demo site for each business and sends timed cold-outreach sequences. Replies are auto-qualified into hot leads." },
  { key: "ad-studio", step: "07", title: "Ad Studio", img: "ad-studio",
    caption: "Ad Studio lets a Marketer agent run autonomous campaigns on Meta and Reddit, driving paid demand straight to your agent-built sites." },
  { key: "orders", step: "08", title: "Orders & Revenue", img: "orders",
    caption: "Orders & Revenue is your scoreboard. Every paid order lands here — the money your agents earn, and the whole loop exists to grow it." },
  { key: "launch", step: "09", title: "Launch Readiness", img: "launch",
    caption: "Launch Readiness shows exactly what's left before you flip from demo to a real, revenue-earning production workspace. Green the whole list and you're live." },
];

function Walkthrough({ onStartTour }) {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [soundOn, setSoundOn] = useState(false);
  const audioRef = useRef(null);
  const DURATION = 5200;

  // Timer-driven progress + advance when sound is OFF.
  useEffect(() => {
    if (!playing || soundOn) return;
    const start = Date.now();
    setProgress(0);
    const t = setInterval(() => {
      const p = Math.min(100, ((Date.now() - start) / DURATION) * 100);
      setProgress(p);
      if (p >= 100) { clearInterval(t); setActive((a) => (a + 1) % SCENES.length); }
    }, 40);
    return () => clearInterval(t);
  }, [playing, active, soundOn]);

  // Audio-driven playback when sound is ON.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (!soundOn) { el.pause(); return; }
    el.src = AUDIO(SCENES[active].key);
    setProgress(0);
    if (playing) el.play().catch(() => {});
  }, [active, soundOn]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !soundOn) return;
    if (playing) el.play().catch(() => {}); else el.pause();
  }, [playing, soundOn]);

  const onTime = () => {
    const el = audioRef.current;
    if (el && el.duration) setProgress(Math.min(100, (el.currentTime / el.duration) * 100));
  };
  const onEnded = () => setActive((a) => (a + 1) % SCENES.length);

  const go = (i) => { setActive(i); setProgress(0); setPlaying(true); };
  const toggleSound = () => {
    setSoundOn((s) => {
      const next = !s;
      if (next) { setPlaying(true); }
      return next;
    });
  };
  const scene = SCENES[active];

  return (
    <div className="hud-card accent-top rounded-xl p-5 sm:p-6" style={{ "--accent-line": "#06B6D4" }} data-testid="guide-walkthrough">
      <audio ref={audioRef} onTimeUpdate={onTime} onEnded={onEnded} preload="none" />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Play className="w-4 h-4 text-cyan-400" />
          <span className="font-display font-bold text-sm">Video Walkthrough</span>
          <span className="font-mono text-[10px] text-slate-500 uppercase tracking-wider">· {active + 1}/{SCENES.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="guide-sound-toggle" onClick={toggleSound}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border font-mono text-[11px] uppercase tracking-wider transition-colors ${soundOn ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-slate-700 bg-[#0E131F] text-slate-300 hover:text-cyan-300 hover:border-cyan-500/40"}`}>
            {soundOn ? <><Volume2 className="w-3.5 h-3.5" /> Sound On</> : <><VolumeX className="w-3.5 h-3.5" /> Play with Sound</>}
          </button>
          <button data-testid="guide-play-toggle" onClick={() => setPlaying((p) => !p)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 font-mono text-[11px] uppercase tracking-wider hover:bg-cyan-500/20 transition-colors">
            {playing ? <><Pause className="w-3.5 h-3.5" /> Pause</> : <><Play className="w-3.5 h-3.5" /> Play</>}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.7fr_1fr] gap-4">
        <div>
          <div className="rounded-lg overflow-hidden border border-slate-800 bg-[#080B11]" data-testid="guide-player">
            <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-800 bg-[#0A0E16]">
              <div className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
              </div>
              <div className="flex-1 rounded-md bg-[#0E131F] border border-slate-800 px-3 py-1 font-mono text-[11px] text-slate-400 truncate">app.kleen-g.com/{scene.key}</div>
              {soundOn && playing && <span className="flex items-center gap-1 text-[10px] font-mono text-emerald-400 uppercase tracking-wider"><Volume2 className="w-3 h-3" /> narrating</span>}
            </div>
            <div className="relative aspect-[1500/940] bg-[#0A0E16]">
              <motion.img key={scene.key} src={IMG(scene.img)} alt={scene.title}
                initial={{ opacity: 0, scale: 1.01 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
                className="absolute inset-0 w-full h-full object-cover object-top" />
            </div>
            <div className="flex gap-1 px-3 py-2.5 bg-[#0A0E16] border-t border-slate-800">
              {SCENES.map((s, i) => (
                <button key={s.key} data-testid={`guide-dot-${i}`} onClick={() => go(i)} className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden" aria-label={s.title}>
                  <div className="h-full rounded-full bg-cyan-500 transition-[width] duration-75" style={{ width: i < active ? "100%" : i === active ? `${progress}%` : "0%" }} />
                </button>
              ))}
            </div>
          </div>
          <motion.div key={scene.key + "-cap"} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="mt-3 flex items-start gap-2.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded bg-cyan-500 text-[#080B11] mt-0.5">Step {scene.step}</span>
            <p className="text-[13px] text-slate-300 leading-relaxed">{scene.caption}</p>
          </motion.div>
        </div>

        <div className="space-y-2">
          <button data-testid="guide-start-tour" onClick={onStartTour}
            className="w-full flex items-center justify-center gap-2 rounded-lg border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 px-3 py-2.5 font-mono text-[11px] uppercase tracking-wider hover:bg-cyan-500/20 transition-colors">
            <Compass className="w-3.5 h-3.5" /> Take the interactive tour
          </button>
          <div className="max-h-[452px] overflow-auto pr-1 space-y-2">
            {SCENES.map((s, i) => (
              <button key={s.key} data-testid={`guide-step-${i}`} onClick={() => go(i)}
                className={`w-full text-left rounded-lg border p-3 transition-all ${i === active ? "border-cyan-500/50 bg-cyan-500/[0.07]" : "border-slate-800 bg-[#080B11] hover:border-slate-700"}`}>
                <div className="flex items-center gap-2">
                  <span className={`font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded ${i === active ? "bg-cyan-500 text-[#080B11]" : "bg-slate-800 text-slate-400"}`}>{s.step}</span>
                  <span className={`font-display font-bold text-[13px] ${i === active ? "text-slate-100" : "text-slate-300"}`}>{s.title}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Written tutorial sections ──
const SECTIONS = [
  { id: "start", icon: LayoutDashboard, route: "/console", img: "console", overline: "Getting Started",
    title: "The Operator Console",
    body: "The Console is your mission control. The top bar shows your live treasury balance and a Fleet Freeze kill-switch. The four tiles track Treasury, Revenue, Spend and Critical Events, while the Live Activity Stream narrates every action your agents take in real time.",
    points: [
      "Watch the onboarding checklist at the top — finish all three to go live.",
      "HITL Spend Queue holds any spend that needs your approval before it executes.",
      "Use Fleet Freeze to instantly halt every agent if anything looks off.",
    ],
    earn: "This is where you confirm the earn-loop is running: Revenue should climb while Spend stays well below it." },
  { id: "agents", icon: Bot, route: "/agents", img: "agents", overline: "Build the Workforce",
    title: "Agents Fleet",
    body: "Agents are wallet-backed AI workers. Give each one a mission and a wallet budget; it then earns, spends within policy, and can spawn specialist child agents. A Builder agent (Forge) ships sites, a Prospector bids on gigs, a Marketer runs ads.",
    points: [
      "Create an agent, set its wallet budget and daily/monthly spend caps.",
      "Active agents earn and deliver; frozen agents are auto-reactivated when you approve their work.",
      "The Spawn Tree shows the parent→child lineage of your fleet.",
    ],
    earn: "More capable active agents = more parallel revenue streams. Fund the ones that convert." },
  { id: "treasury", icon: Wallet, route: "/treasury", img: "treasury", overline: "Fund & Protect",
    title: "Treasury & Guardrails",
    body: "The treasury is your shared capital pool. Top it up, then set the guardrails: per-agent spend caps, an approval threshold above which spend is held for your sign-off, and demo vs. production mode.",
    points: [
      "Add funds to the treasury — agents auto-top-up their wallets from it when they need to work.",
      "Set an approval threshold so any large spend pauses for a human.",
      "Keep 'Require approval for production deploys' on until you trust the loop.",
    ],
    earn: "Guardrails let you scale spend confidently — you cap downside while agents chase upside." },
  { id: "deploy", icon: Rocket, route: "/deployments", img: "deployments", overline: "Ship Product",
    title: "Deployment Pipeline",
    body: "Agents generate complete sites and ship them to Vercel with automatic SSL at a branded subdomain. Every production deploy is held for your approval. Each card shows the owning agent's remaining daily deploy budget and a full Money Trail.",
    points: [
      "Approve a card to build & ship; the deploy cost is charged only when it actually ships.",
      "Redeploy pushes a new version; Retry re-attempts a blocked/failed card in one click.",
      "The Deploy Cost Meter warns you before an agent runs low on daily budget.",
    ],
    earn: "Shipped sites are the deliverable clients pay for — this pipeline turns awarded work into revenue." },
  { id: "leads", icon: Radar, route: "/lead-sources", img: "lead-sources", overline: "Fill the Funnel",
    title: "Lead Sources",
    body: "The top of your revenue funnel. Connect Google Places to discover real local businesses that need a website, and connect Freelancer.com so the Prospector agent can bid on gigs automatically within your score and volume filters.",
    points: [
      "Add your Google Places API key to unlock local business discovery.",
      "Set the Prospector's min-score filter and daily bid cap; review its Run Summary.",
      "Qualified leads flow into the Customer Inbox and Outreach engine.",
    ],
    earn: "No leads, no revenue. This is where new earning opportunities enter the system." },
  { id: "outreach", icon: Target, route: "/outreach", img: "outreach", overline: "Convert Leads",
    title: "Local Outreach Engine",
    body: "For each discovered business, an agent auto-generates a personalized demo site and runs a timed cold-outreach email sequence (respecting business-hours send windows). Replies are auto-qualified so hot prospects surface immediately.",
    points: [
      "Turn on Auto-Send to let sequences run without one-click drafting.",
      "Edit the sequence steps and set your timezone send window.",
      "A verified Resend sending domain keeps deliverability high.",
    ],
    earn: "This converts cold local businesses into paying website clients on autopilot." },
  { id: "ads", icon: Megaphone, route: "/ad-studio", img: "ad-studio", overline: "Scale Demand",
    title: "Ad Studio",
    body: "Launch autonomous marketing campaigns through a Marketer agent across Meta Ads and Reddit Ads. Set a daily ad cap and let the agent drive traffic to your agent-built sites and offers.",
    points: [
      "Connect your ad accounts, then set a daily ad spend cap.",
      "The Marketer agent creates and manages campaigns under policy.",
      "Track ad spend against attributed revenue in ROI & Economics.",
    ],
    earn: "Paid demand accelerates the funnel when organic outreach isn't enough." },
  { id: "orders", icon: ShoppingCart, route: "/orders", img: "orders", overline: "Collect Revenue",
    title: "Orders & Revenue",
    body: "Every paid order — subscriptions, one-off projects, deployed sites — is recorded here with its status. This is the scoreboard for the entire platform.",
    points: [
      "Real paid orders (not simulations) count toward your go-live milestone.",
      "Renewals and dunning run automatically once your Stripe webhook is set.",
      "Filter by status to see pending vs. paid revenue.",
    ],
    earn: "This is the money. Everything upstream exists to grow this total." },
  { id: "billing", icon: CreditCard, route: "/billing", img: "billing", overline: "Monetize",
    title: "Billing & Stripe",
    body: "Connect your own live Stripe key to collect real money and set the webhook secret to enable durable payment links, renewals and dunning. Until then the shared sandbox key lets you test the full flow safely.",
    points: [
      "Swap the sandbox key for your live Stripe key when you're ready for real revenue.",
      "Set STRIPE_WEBHOOK_SECRET to activate subscriptions and automatic retries.",
      "Pick your plan — higher plans raise your agent cap so you can run a bigger fleet.",
    ],
    earn: "This is the switch that turns simulated revenue into real deposits in your account." },
  { id: "launch", icon: Gauge, route: "/launch", img: "launch", overline: "Go Live",
    title: "Launch Readiness",
    body: "A single dashboard of every go-live check — grouped into Money, Deploy, Outreach, Agents and Config — with a progress bar. Each unchecked item links straight to where you fix it.",
    points: [
      "Green the whole list: live Stripe key, webhook, Vercel, Places, Resend domain, an active agent and a real sale.",
      "Turn off Demo mode so only genuine revenue counts.",
      "When it hits 100%, you're production-ready.",
    ],
    earn: "Completing this checklist is the moment your workspace starts earning real money." },
];

export default function Guide() {
  const navigate = useNavigate();
  const startTour = () => window.dispatchEvent(new Event("kleeng:start-tour"));
  return (
    <div className="space-y-6 pb-10" data-testid="guide-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Overline>Docs · Tutorial · Video Walkthrough</Overline>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1 flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-cyan-400" /> How to Use, Earn & Monetize Kleen-G
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">A complete walkthrough of the platform — from spinning up your first agent to shipping paid sites and flipping the switch to real revenue.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="guide-header-tour" onClick={startTour}
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 font-mono text-[11px] uppercase tracking-wider hover:bg-cyan-500/20 transition-colors">
            <Compass className="w-3.5 h-3.5" /> Take the tour
          </button>
          <a data-testid="download-html" href={DOC_HTML} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg border border-slate-700 bg-[#0E131F] text-slate-200 font-mono text-[11px] uppercase tracking-wider hover:border-cyan-500/40 hover:text-cyan-300 transition-colors">
            <FileText className="w-3.5 h-3.5" /> Open Doc (print / PDF)
          </a>
          <a data-testid="download-md" href={DOC_MD} download
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg border border-slate-700 bg-[#0E131F] text-slate-200 font-mono text-[11px] uppercase tracking-wider hover:border-cyan-500/40 hover:text-cyan-300 transition-colors">
            <Download className="w-3.5 h-3.5" /> Markdown
          </a>
        </div>
      </div>

      <Walkthrough onStartTour={startTour} />

      {/* The earn loop */}
      <div className="hud-card rounded-xl p-5 sm:p-6" data-testid="guide-earnloop">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-4 h-4 text-emerald-400" />
          <span className="font-display font-bold text-sm">The Earn Loop — how money actually flows</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[12px] font-mono">
          {["Find leads", "Send outreach / bid", "Win the client", "Agent builds site", "You approve & ship", "Client pays", "Treasury grows"].map((s, i, arr) => (
            <span key={s} className="flex items-center gap-2">
              <span className={`px-3 py-1.5 rounded-md border ${i === arr.length - 1 ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-slate-700 bg-[#0E131F] text-slate-300"}`}>{s}</span>
              {i < arr.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-slate-600" />}
            </span>
          ))}
        </div>
        <p className="text-[13px] text-slate-400 mt-4 leading-relaxed">Fund the treasury once. Agents pull small budgets to find work and build. You stay in the loop only for production ships and large spends. Every completed cycle deposits revenue back into the treasury — which funds the next, larger cycle.</p>
      </div>

      {/* Sections */}
      <div className="space-y-5">
        {SECTIONS.map((s, i) => {
          const Icon = s.icon;
          const flip = i % 2 === 1;
          return (
            <div key={s.id} id={s.id} className="hud-card rounded-xl overflow-hidden" data-testid={`guide-section-${s.id}`}>
              <div className={`grid lg:grid-cols-2 ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div className="p-6 sm:p-7">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center"><Icon className="w-4 h-4 text-cyan-400" /></span>
                    <Overline>{s.overline}</Overline>
                  </div>
                  <h2 className="font-display text-xl font-bold mt-3">{s.title}</h2>
                  <p className="text-[13px] text-slate-400 mt-2 leading-relaxed">{s.body}</p>
                  <ul className="mt-4 space-y-2">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-[13px] text-slate-300">
                        <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" /> <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 rounded-lg border border-emerald-500/25 bg-emerald-500/[0.06] px-3.5 py-3 flex items-start gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <p className="text-[12.5px] text-emerald-200/90 leading-relaxed"><b className="text-emerald-300">How it earns:</b> {s.earn}</p>
                  </div>
                  <button data-testid={`guide-goto-${s.id}`} onClick={() => navigate(s.route)}
                    className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-wider text-cyan-400 hover:text-cyan-300 transition-colors">
                    Open {s.title} <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="relative bg-[#0A0E16] border-t lg:border-t-0 lg:border-l border-slate-800 flex items-center p-4">
                  <img src={IMG(s.img)} alt={s.title} loading="lazy"
                    className="w-full rounded-lg border border-slate-800 shadow-2xl" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
